import { type ReactNode } from 'react';
import TelemetryTile from './TelemetryTile';
import { useGcsStore } from '../../../store/useGcsStore';
import { BatteryChargingState, type VehicleId } from '../../../types/gcs/vehicle';
import { formatPercentage, getUnitFormatters } from '../../../utils/formatters';
import { en } from '../../../locales/en';
import { selectUnitSystem } from '../../../store/selectors/selectUnitSystem';
import { selectVehicleStatusCategory } from '../../../store/selectors/selectVehicleStatusCategory';
import { VehicleStatusCategory } from '../../../types/ui/vehicleStatusCategory';
import { BatteryCharging01, BatteryEmpty, Speedometer01, SwitchVertical01 } from '@untitledui/icons';
import { Icon } from '../../../components/general/Icon';
import { getBatteryFillColor, getBatteryFillWidth } from '../../../utils/battery';

interface SidePanelVehicleTelemetryTileProps {
  gcsId: string;
  vehicleId: VehicleId;
}

const TELEMETRY_ICON_SIZE = 16;

export const SidePanelVehicleTelemetryTile = ({ gcsId, vehicleId }: SidePanelVehicleTelemetryTileProps) => {
  const unitSystem = useGcsStore((state) => selectUnitSystem(state.data, gcsId));
  const isImperialUnits = unitSystem === 'imperial';
  const unitFormatters = getUnitFormatters(unitSystem);
  const telemetrySpeedTitle = isImperialUnits ? en.telemetry.speedMph : en.telemetry.speedMs;
  const telemetryAltitudeTitle = isImperialUnits ? en.telemetry.altitudeFt : en.telemetry.altitudeM;

  const liveMission = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;
    return gcsData.liveMissionsMap.get(vehicleId);
  });

  const isInflight = useGcsStore((state) => {
    const category = selectVehicleStatusCategory(state.data, gcsId, vehicleId);
    return (
      category === VehicleStatusCategory.INFLIGHT_NOMINAL || category === VehicleStatusCategory.INFLIGHT_FAILURE_MODE
    );
  });

  // For inflight: get data from mission flight data
  const flightData = useGcsStore((state) => {
    if (!isInflight || !liveMission) return null;
    return state.data.get(gcsId)?.missionFlightDataMap.get(liveMission.missionId);
  });

  // For on ground: get battery data from vehicle mission computer status
  const batteryChargePct = useGcsStore(
    (state) => state.data.get(gcsId)?.vehicleMissionComputerStatusMap.get(vehicleId)?.batteryChargePct
  );

  const batteryChargingState = useGcsStore(
    (state) => state.data.get(gcsId)?.vehicleMissionComputerStatusMap.get(vehicleId)?.batteryChargingState
  );

  const getBatteryIcon = (batteryPct: number | null | undefined): ReactNode => {
    const size = TELEMETRY_ICON_SIZE;

    if (batteryChargingState === BatteryChargingState.CHARGING) {
      return <Icon icon={BatteryCharging01} size={size} />;
    }

    if (batteryPct === null || batteryPct === undefined) {
      return <Icon icon={BatteryEmpty} size={size} />;
    }

    // Battery outline uses a 24×24 viewBox; body inner area ≈ x=4,y=9,w=14,h=6
    const fillColor = getBatteryFillColor(batteryPct);
    const fillWidthSvg = getBatteryFillWidth(batteryPct);

    return (
      <div style={{ position: 'relative', width: size, height: size, display: 'inline-flex', flexShrink: 0 }}>
        <BatteryEmpty style={{ width: size, height: size, color: 'var(--icon-color-primary)' }} />
        <svg
          viewBox='0 0 24 24'
          width={size}
          height={size}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          aria-hidden='true'
        >
          <rect x={4} y={9} width={fillWidthSvg} height={6} rx={2} ry={2} fill={fillColor} />
        </svg>
      </div>
    );
  };

  // Get battery value based on state
  let batteryValue: string = en.common.notAvailable;
  let batteryColor = undefined;

  if (isInflight && flightData) {
    batteryValue = formatPercentage(flightData.batteryPct);
  } else if (!isInflight && batteryChargePct) {
    batteryValue = formatPercentage(batteryChargePct);
    batteryColor = batteryChargingState === BatteryChargingState.ERROR ? 'var(--color-error)' : undefined;
  }

  const batteryPctForIcon = isInflight ? flightData?.batteryPct : batteryChargePct;
  const batteryIcon = getBatteryIcon(batteryPctForIcon);

  // Get speed and altitude values
  const speedValue = isInflight && flightData ? unitFormatters.formatSpeed(flightData.speedMs) : en.common.notAvailable;
  const altitudeValue =
    isInflight && flightData ? unitFormatters.formatAltitude(flightData.location.alt) : en.common.notAvailable;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <TelemetryTile
        icon={<Icon icon={Speedometer01} size={TELEMETRY_ICON_SIZE} />}
        value={speedValue}
        title={telemetrySpeedTitle}
      />
      <TelemetryTile
        icon={<Icon icon={SwitchVertical01} size={TELEMETRY_ICON_SIZE} />}
        value={altitudeValue}
        title={telemetryAltitudeTitle}
      />
      <TelemetryTile icon={batteryIcon} value={batteryValue} color={batteryColor} title={en.telemetry.batteryPct} />
    </div>
  );
};
